import React, { useMemo, useState } from 'react';
import styles from './PACSTable.module.scss';

export interface PACSColumn<T extends Record<string, any>> {
  id: string;
  label: string;
  data?: React.ReactNode[];
}

export interface PACSTableProps<T extends Record<string, any>> {
  columns: PACSColumn<T>[];
  data?: T[];
  className?: string;
  getRowId?: (row: T, index: number) => React.Key;
  onRowClick?: (key: React.Key, row: T) => void;
  selectedKey?: React.Key | React.Key[];
}

function PACSTable<T extends Record<string, any>>({
  columns,
  data = [],
  className = '',
  getRowId,
  onRowClick,
  selectedKey,
}: PACSTableProps<T>): React.JSX.Element {
  const [hoveredKey, setHoveredKey] = useState<React.Key | undefined>(
    undefined,
  );

  const resolvedData = useMemo<T[]>(() => {
    if (data.length > 0) return data;

    const hasColumnData = columns.some((col) => Array.isArray(col.data));
    if (!hasColumnData) return [];

    const rowCount = columns.reduce(
      (max, col) => Math.max(max, col.data?.length ?? 0),
      0,
    );

    return Array.from({ length: rowCount }, (_, rowIndex) => {
      const row = {} as Record<string, React.ReactNode>;
      columns.forEach((col) => {
        row[col.id] = col.data?.[rowIndex];
      });
      return row as T;
    });
  }, [columns, data]);

  const getKey = getRowId ?? ((_, i) => i);

  const isRowSelected = (
    key: React.Key,
    selected?: React.Key | React.Key[],
  ) => {
    if (!selected) return false;
    if (Array.isArray(selected)) {
      return selected.map(String).includes(String(key));
    }
    return String(selected) === String(key);
  };

  const renderCellValue = (colId: string, value: React.ReactNode) => {
    if (colId === 'status') {
      return (
        <span
          className={`${styles.bodyLabel} ${
            value === 'success'
              ? styles.statusSuccess
              : value === 'fail'
                ? styles.statusFail
                : ''
          }`}
          title={String(value ?? '')}
        >
          <span className={styles.statusDot} />
        </span>
      );
    }
    if (colId === 'defaultServer') {
      return value ? (
        <span className={`${styles.bodyLabel} ${styles.defaultServerBadge}`} title="Default">
          Default
        </span>
      ) : (
        <span className={styles.bodyLabel} title="-">
          -
        </span>
      );
    }

    const textValue = String(value ?? '');
    return (
      <span className={styles.bodyLabel} title={textValue}>
        {textValue}
      </span>
    );
  };

  return (
    <div className={`${styles.tableWrap} ${className}`}>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.id} className={styles[`col-${col.id}`] ?? ''} title={col.label}>
                <span className={styles.headerLabel}>{col.label}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {resolvedData.map((row, rowIndex) => {
            const key = getKey(row, rowIndex);
            const isSelected = isRowSelected(key, selectedKey);
            const isHovered =
              hoveredKey !== undefined && String(hoveredKey) === String(key);

            return (
              <tr
                key={String(key)}
                className={`${isHovered ? styles.hovered : ''} ${
                  isSelected ? styles.selected : ''
                }`}
                onClick={onRowClick ? () => onRowClick(key, row) : undefined}
                onMouseEnter={() => setHoveredKey(key)}
                onMouseLeave={() =>
                  setHoveredKey((prev) =>
                    prev !== undefined && String(prev) === String(key)
                      ? undefined
                      : prev,
                  )
                }
              >
                {columns.map((col) => (
                  <td key={`${String(key)}-${col.id}`} className={styles[`col-${col.id}`] ?? ''}>
                    <div className={styles.cell}>
                      {renderCellValue(col.id, row[col.id as keyof T])}
                    </div>
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default PACSTable;
